import { useState } from "react";
import type { Recipe } from "@/api/recipes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface RecipeFormProps {
  recipe?: Recipe;
  onSave: (data: { title: string; ingredients: string[]; instructions: string[] }) => void;
  onCancel: () => void;
}

export function RecipeForm({ recipe, onSave, onCancel }: RecipeFormProps) {
  const [title, setTitle] = useState(recipe?.title ?? "");
  const [ingredients, setIngredients] = useState(recipe?.ingredients.join("\n") ?? "");
  const [instructions, setInstructions] = useState(recipe?.instructions.join("\n") ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEdit = !!recipe;

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors.title = "Введите название рецепта";
    if (!ingredients.trim()) newErrors.ingredients = "Добавьте хотя бы один ингредиент";
    if (!instructions.trim()) newErrors.instructions = "Опишите хотя бы один шаг";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    onSave({
      title: title.trim(),
      ingredients: ingredients
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      instructions: instructions
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
    });
  };

  return (
    <div className="animate-fade-in">
      <button
        onClick={onCancel}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 mb-6 group"
        style={{ fontFamily: "var(--font-body)" }}
      >
        <svg
          className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform duration-200"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
        Назад
      </button>

      <h1
        className="text-3xl font-bold mb-8"
        style={{ fontFamily: "var(--font-display)", fontWeight: 900 }}
      >
        {isEdit ? "Редактировать рецепт" : "Новый рецепт"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        {/* Title */}
        <div className="space-y-2 animate-fade-in-up stagger-1">
          <Label
            htmlFor="title"
            className="text-sm font-medium"
            style={{ fontFamily: "var(--font-body)" }}
          >
            Название
          </Label>
          <Input
            id="title"
            type="text"
            placeholder="Например: Борщ классический"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (errors.title) setErrors((prev) => ({ ...prev, title: "" }));
            }}
            className={`h-11 bg-card border-border/60 focus-visible:ring-primary/30 ${errors.title ? "border-destructive" : ""}`}
            style={{ fontFamily: "var(--font-body)" }}
          />
          {errors.title && (
            <p className="text-xs text-destructive" style={{ fontFamily: "var(--font-body)" }}>
              {errors.title}
            </p>
          )}
        </div>

        {/* Ingredients */}
        <div className="space-y-2 animate-fade-in-up stagger-2">
          <Label
            htmlFor="ingredients"
            className="text-sm font-medium"
            style={{ fontFamily: "var(--font-body)" }}
          >
            Ингредиенты
          </Label>
          <p className="text-xs text-muted-foreground" style={{ fontFamily: "var(--font-body)" }}>
            Каждый ингредиент на новой строке
          </p>
          <Textarea
            id="ingredients"
            placeholder={"500 г муки\n3 яйца\n200 мл молока"}
            value={ingredients}
            onChange={(e) => {
              setIngredients(e.target.value);
              if (errors.ingredients) setErrors((prev) => ({ ...prev, ingredients: "" }));
            }}
            rows={6}
            className={`bg-card border-border/60 focus-visible:ring-primary/30 resize-y ${errors.ingredients ? "border-destructive" : ""}`}
            style={{ fontFamily: "var(--font-body)" }}
          />
          {errors.ingredients && (
            <p className="text-xs text-destructive" style={{ fontFamily: "var(--font-body)" }}>
              {errors.ingredients}
            </p>
          )}
        </div>

        {/* Instructions */}
        <div className="space-y-2 animate-fade-in-up stagger-3">
          <Label
            htmlFor="instructions"
            className="text-sm font-medium"
            style={{ fontFamily: "var(--font-body)" }}
          >
            Инструкция
          </Label>
          <p className="text-xs text-muted-foreground" style={{ fontFamily: "var(--font-body)" }}>
            Каждый шаг на новой строке
          </p>
          <Textarea
            id="instructions"
            placeholder={"Замесите тесто из муки и яиц\nРаскатайте тонко\nВыпекайте 20 минут при 180°C"}
            value={instructions}
            onChange={(e) => {
              setInstructions(e.target.value);
              if (errors.instructions) setErrors((prev) => ({ ...prev, instructions: "" }));
            }}
            rows={8}
            className={`bg-card border-border/60 focus-visible:ring-primary/30 resize-y ${errors.instructions ? "border-destructive" : ""}`}
            style={{ fontFamily: "var(--font-body)" }}
          />
          {errors.instructions && (
            <p className="text-xs text-destructive" style={{ fontFamily: "var(--font-body)" }}>
              {errors.instructions}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2 animate-fade-in-up stagger-4">
          <Button
            type="submit"
            className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5"
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            {isEdit ? "Сохранить изменения" : "Сохранить рецепт"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="border-border/60"
          >
            Отмена
          </Button>
        </div>
      </form>
    </div>
  );
}
